import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Tenant selection state for tracking current and previous tenant
 */
export interface TenantSelectionState {
    /** Currently selected tenant ID */
    selectedTenantId: string | null;
    /** Previously selected tenant ID (before latest change) */
    previousTenantId: string | null;
}

export const initialTenantSelectionState: TenantSelectionState = {
    selectedTenantId: null,
    previousTenantId: null,
};

const tenantSelectionSlice = createSlice({
    name: 'tenantSelection',
    initialState: initialTenantSelectionState,
    reducers: {
        /**
         * Set the selected tenant, automatically tracking the previous value
         */
        setSelectedTenant: (state, action: PayloadAction<string | null>) => {
            // Only update previous if the value is actually changing
            if (state.selectedTenantId !== action.payload) {
                state.previousTenantId = state.selectedTenantId;
                state.selectedTenantId = action.payload;
            }
        },

        /**
         * Reset tenant selection state
         */
        resetTenantSelection: (state) => {
            state.selectedTenantId = null;
            state.previousTenantId = null;
        },

        /**
         * Acknowledge the tenant change by syncing previousTenantId to selectedTenantId
         */
        acknowledgeTenantChange: (state) => {
            state.previousTenantId = state.selectedTenantId;
        },
    },
});

export const {
    setSelectedTenant,
    resetTenantSelection,
    acknowledgeTenantChange,
} = tenantSelectionSlice.actions;

export default tenantSelectionSlice.reducer;

// Selectors
export const selectSelectedTenantId = (state: { copilotTenantSelection: TenantSelectionState }) =>
    state.copilotTenantSelection?.selectedTenantId ?? null;

export const selectPreviousTenantId = (state: { copilotTenantSelection: TenantSelectionState }) =>
    state.copilotTenantSelection?.previousTenantId ?? null;

export const selectTenantSelection = (state: { copilotTenantSelection: TenantSelectionState }) =>
    state.copilotTenantSelection ?? initialTenantSelectionState;
